import React, { useEffect, useMemo, useState } from "react";
import { Table, Button, Modal, Form, Input, Select, message, Popconfirm, Result } from "antd";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import AxiosOICStat from "AxiosOICStat";
import RoleUtils from "utils/RoleUtils";
import { ROLE_LABELS, ROLE_OPTIONS } from "constants/roles";
import { FilterDropdown } from "components/FilterDropdown";
import { createFilterOptions } from "components/Utils";

const { Option } = Select;
const { Search } = Input;

const ROLE_FILTER_OPTIONS = Object.entries(ROLE_LABELS).map(([value, text]) => ({ text, value: Number(value) }));

export const AdminPanel = () => {
    const user = useSelector((state) => state.user);
    const isAdmin = RoleUtils.isAdmin(user.role);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [searchValue, setSearchValue] = useState("");

    const [form] = Form.useForm();

    // Fetch users
    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await AxiosOICStat.get("/users");
            setUsers(response.data);
        } catch (error) {
            message.error("Failed to fetch users.");
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // Handle edit
    const handleEdit = (user) => {
        setEditingUser(user);
        form.setFieldsValue(user);
        setIsModalOpen(true);
    };

    // Handle delete
    const handleDelete = async (userId) => {
        try {
            await AxiosOICStat.delete(`/users/${userId}`);
            message.success("User deleted successfully.");
            fetchUsers();
        } catch (error) {
            message.error("Failed to delete user.");
            console.error(error);
        }
    };

    // Handle form submission (edit/create)
    const handleFormSubmit = async (values) => {
        try {
            if (editingUser) {
                // Update user
                await AxiosOICStat.put(`/users/${editingUser.id}`, values);
                message.success("User updated successfully.");
            } else {
                // Create user
                await AxiosOICStat.post("/users", values);
                message.success("User created successfully.");
            }
            setIsModalOpen(false);
            fetchUsers();
        } catch (error) {
            message.error(error?.response?.data?.detail || "Failed to save user.");
            console.error(error);
        }
    };

    // Open modal for creating a new user
    const handleAddNewUser = () => {
        setEditingUser(null);
        form.resetFields();
        setIsModalOpen(true);
    };

    // Fetch users once the current user is known to be an admin
    useEffect(() => {
        if (isAdmin) fetchUsers();
    }, [isAdmin]);

    const filteredUsers = useMemo(() => {
        const lower = searchValue.trim().toLowerCase();
        if (!lower) return users;
        return users.filter((u) =>
            [u.username, u.first_name, u.last_name, u.email].some((field) => (field || "").toLowerCase().includes(lower))
        );
    }, [users, searchValue]);

    const textColumn = (title, key) => {
        const options = createFilterOptions(users.filter((u) => u[key]), key);
        return {
            title,
            dataIndex: key,
            key,
            sorter: (a, b) => (a[key] || "").localeCompare(b[key] || ""),
            onFilter: (value, record) => record[key] === value,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <FilterDropdown
                    options={options}
                    setSelectedKeys={setSelectedKeys}
                    selectedKeys={selectedKeys}
                    confirm={confirm}
                    clearFilters={clearFilters}
                />
            ),
        };
    };

    // Table columns
    const columns = [
        textColumn("Username", "username"),
        textColumn("First Name", "first_name"),
        textColumn("Last Name", "last_name"),
        {
            title: "Role",
            dataIndex: "role",
            key: "role",
            render: (role) => RoleUtils.getRoleLabel(role),
            filters: ROLE_FILTER_OPTIONS,
            onFilter: (value, record) => record.role === value,
            sorter: (a, b) => RoleUtils.getRoleLabel(a.role).localeCompare(RoleUtils.getRoleLabel(b.role)),
        },
        {
            title: "Last Login",
            dataIndex: "last_login",
            key: "last_login",
            render: (lastLogin) => (lastLogin ? dayjs(lastLogin).format("YYYY-MM-DD HH:mm") : "-"),
            sorter: (a, b) => dayjs(a.last_login || 0).valueOf() - dayjs(b.last_login || 0).valueOf(),
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, user) => (
                <>
                    <Button type="link" onClick={() => handleEdit(user)}>
                        Edit
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to delete this user?"
                        onConfirm={() => handleDelete(user.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="link" danger>
                            Delete
                        </Button>
                    </Popconfirm>
                </>
            ),
        },
    ];

    // Role is still loading: render nothing rather than flashing the 403 page at an admin.
    if (!isAdmin) {
        if (user.status !== 'succeeded' && user.status !== 'failed') return null;
        return <Result status="403" title="403" subTitle="You do not have permission to access this page." />;
    }

    return (
        <div>
            <h1>Admin Panel</h1>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                <Button type="primary" onClick={handleAddNewUser}>
                    Add New User
                </Button>
                <Search
                    placeholder="Search users"
                    allowClear
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    style={{ maxWidth: 300 }}
                />
            </div>
            <Table
                dataSource={filteredUsers}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
                scroll={{ x: "max-content" }}
            />
            <Modal
                title={editingUser ? "Edit User" : "Add New User"}
                visible={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onOk={() => form.submit()}
            >
                <Form form={form} onFinish={handleFormSubmit} layout="vertical">
                    <Form.Item
                        name="username"
                        label="Username"
                        rules={[
                            { required: true, message: "Please enter a username." },
                            { pattern: /^\S+$/, message: "Username cannot contain spaces." },
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[{ type: "email", message: "Please enter a valid email." }]}
                        normalize={(value) => value || undefined}
                    >
                        <Input type="email" />
                    </Form.Item>
                    <Form.Item
                        name="password"
                        label="Password"
                        rules={[
                            { required: !editingUser, message: "Please enter a password." },
                        ]}
                    >
                        <Input.Password />
                    </Form.Item>
                    <Form.Item
                        name="first_name"
                        label="First Name"
                        rules={[{ required: true, message: "Please enter the first name." }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="last_name"
                        label="Last Name"
                        rules={[{ required: true, message: "Please enter the last name." }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="role"
                        label="Role"
                        rules={[{ required: true, message: "Please select a role." }]}
                    >
                        <Select>
                            {ROLE_OPTIONS.map(({ value, label }) => (
                                <Option key={value} value={value}>{label}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
